import { Container, Button, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { CreateManualInvestmentRequestBody } from '@inversor-hub/types';
import { useCreateManualInvestment, useUploadInvestmentMainImage } from '@/lib/api/investments.ts';
import { GoogleMapsProvider } from '@/context/GoogleMapsProvider.tsx';
import InvestmentForm from '@/features/investments/form/components/InvestmentForm.tsx';
import { useInvestmentForm } from '@/features/investments/form/useInvestmentForm.ts';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import { ANALYTICS_PLATFORM, trackProductEvent } from '@/lib/analytics/product-events';

const CreatePsiInvestmentPage = () => {
  const navigate = useNavigate();
  const createInvestment = useCreateManualInvestment();
  const uploadMainImage = useUploadInvestmentMainImage();
  const { show: showSnackbar } = useSnackbar();
  const { trackEvent } = useAnalytics();

  const { initialFormData, handleSubmit, handleErrorDismiss, error, isLoading } = useInvestmentForm(
    {
      onSubmit: async ({ payload, mainImageFile }) => {
        const investment = await createInvestment.mutateAsync(
          payload as CreateManualInvestmentRequestBody
        );

        if (mainImageFile && investment?.id) {
          await uploadMainImage.mutateAsync({ investmentId: investment.id, file: mainImageFile });
        }

        trackProductEvent(trackEvent, 'investment_created', {
          investment_id: investment.id,
          investment_type: investment.type,
          entry_point: 'portfolio',
          role: 'psi',
          platform: ANALYTICS_PLATFORM,
        });
      },
      onSuccess: () => {
        navigate('/inversiones');
        showSnackbar({ title: 'Operación registrada', variant: 'success' });
      },
    }
  );

  const handleCancel = () => {
    navigate('/inversiones');
  };

  return (
    <GoogleMapsProvider>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ mb: 2 }}>
            Volver a operaciones
          </Button>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Nueva operación
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Registra una operación para gestionarla en tu cartera
          </Typography>
        </Box>

        <InvestmentForm
          initialData={initialFormData}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitLabel="Crear operación"
          loadingLabel="Guardando..."
          isLoading={isLoading}
          error={error}
          onErrorDismiss={handleErrorDismiss}
        />
      </Container>
    </GoogleMapsProvider>
  );
};

export default CreatePsiInvestmentPage;
